import { useEffect, useMemo, useState } from "react";
import { FiPlusCircle, FiRepeat } from "react-icons/fi";
import AppLayout from "../components/common/AppLayout";
import ConfirmModal from "../components/common/ConfirmModal";
import MovimientoFilters from "../components/movimientos/MovimientoFilters";
import MovimientoTable from "../components/movimientos/MovimientoTable";
import MovimientoForm from "../components/movimientos/MovimientoForm";
import useMovimientos from "../hooks/useMovimientos";
import { apiFetch } from "../api/apiClient";

export default function MovimientosPage() {
    const {
        movimientos,
        loading,
        filtros,
        setFiltros,
        guardarMovimiento,
        anular,
        recargar,
    } = useMovimientos();

    const [openForm, setOpenForm] = useState(false);
    const [editing, setEditing] = useState(null);

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [movimientoAAnular, setMovimientoAAnular] = useState(null);

    const [cuentas, setCuentas] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [subcategorias, setSubcategorias] = useState([]);
    const [monedas, setMonedas] = useState([]);
    const [metodosPago, setMetodosPago] = useState([]);
    const [tiposComprobante, setTiposComprobante] = useState([]);

    useEffect(() => {
        async function cargarCatalogos() {
            try {
                const [
                    cuentasResp,
                    categoriasResp,
                    subcategoriasResp,
                    monedasResp,
                    metodosPagoResp,
                    tiposComprobanteResp,
                ] = await Promise.all([
                    apiFetch("/api/cuentas"),
                    apiFetch("/api/catalogos/categorias"),
                    apiFetch("/api/catalogos/subcategorias"),
                    apiFetch("/api/catalogos/monedas"),
                    apiFetch("/api/catalogos/metodos-pago"),
                    apiFetch("/api/catalogos/tipos-comprobante"),
                ]);

                setCuentas(Array.isArray(cuentasResp) ? cuentasResp : []);
                setCategorias(Array.isArray(categoriasResp) ? categoriasResp : []);
                setSubcategorias(Array.isArray(subcategoriasResp) ? subcategoriasResp : []);
                setMonedas(Array.isArray(monedasResp) ? monedasResp : []);
                setMetodosPago(Array.isArray(metodosPagoResp) ? metodosPagoResp : []);
                setTiposComprobante(Array.isArray(tiposComprobanteResp) ? tiposComprobanteResp : []);
            } catch (e) {
                console.error("Error cargando catálogos de movimientos", e);
            }
        }

        cargarCatalogos();
    }, []);

    const titulo = useMemo(
        () => (editing ? "Editar movimiento" : "Nuevo movimiento"),
        [editing]
    );

    return (
        <AppLayout
            title={
                <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                    <FiRepeat size={30} />
                    Movimientos
                </span>
            }
        >
            <MovimientoFilters
                filtros={filtros}
                setFiltros={setFiltros}
                onBuscar={recargar}
                cuentas={cuentas}
                categorias={categorias}
            />

            <div className="toolbar-row">
                <button
                    className="btn-primary"
                    type="button"
                    onClick={() => {
                        setEditing(null);
                        setOpenForm(true);
                    }}
                >
                    <FiPlusCircle size={18} />
                    Nuevo movimiento
                </button>
            </div>

            <MovimientoTable
                data={movimientos}
                loading={loading}
                onEdit={(row) => {
                    setEditing(row);
                    setOpenForm(true);
                }}
                onAnular={(row) => {
                    setMovimientoAAnular(row);
                    setConfirmOpen(true);
                }}
            />

            {openForm && (
                <MovimientoForm
                    title={titulo}
                    initialData={editing}
                    cuentas={cuentas}
                    categorias={categorias}
                    subcategorias={subcategorias}
                    monedas={monedas}
                    metodosPago={metodosPago}
                    tiposComprobante={tiposComprobante}
                    onClose={() => setOpenForm(false)}
                    onSave={async (payload) => {
                        await guardarMovimiento(payload, editing?.idMovimiento);
                        setOpenForm(false);
                    }}
                />
            )}

            <ConfirmModal
                open={confirmOpen}
                title="Anular movimiento"
                message={`¿Seguro que deseas anular el movimiento "${movimientoAAnular?.descripcion || ""}"? Esta acción afecta información sensible.`}
                confirmText="Sí, anular"
                cancelText="No, cancelar"
                variant="danger"
                onClose={() => {
                    setConfirmOpen(false);
                    setMovimientoAAnular(null);
                }}
                onConfirm={async () => {
                    await anular(movimientoAAnular.idMovimiento);
                    setConfirmOpen(false);
                    setMovimientoAAnular(null);
                }}
            />
        </AppLayout>
    );
}